import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import { normalizePro20Response } from '@/shared/api/pro20-transform';
import type { Pro20ApiResponse } from '@/shared/types/api-response.types';

export interface BranchInformativa {
  name: string;
  companyName: string;
  email: string;
  address: string;
  city: string;
  cap: string;
  phoneNumber: string;
  vat: string;
}

function asTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** Prefer companyName, else branch name (`name`), else optional fallback. */
export function resolveInformativaDisplayName(
  informativa: BranchInformativa | null | undefined,
  fallbackName = '',
): string {
  const companyName = informativa?.companyName.trim() ?? '';
  if (companyName) {
    return companyName;
  }
  const name = informativa?.name.trim() ?? '';
  if (name) {
    return name;
  }
  const fallback = fallbackName.trim();
  return fallback || '—';
}

function normalizeInformativa(raw: unknown): BranchInformativa | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const row = raw as Record<string, unknown>;

  return {
    name: asTrimmedString(row.name),
    companyName: asTrimmedString(row.companyName),
    email: asTrimmedString(row.email),
    address: asTrimmedString(row.address),
    city: asTrimmedString(row.city),
    cap: asTrimmedString(row.cap),
    phoneNumber: asTrimmedString(row.phoneNumber),
    vat: asTrimmedString(row.vat),
  };
}

export const informativaApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getInformativaByBranchCode: builder.query<
      BranchInformativa,
      { branchCode: string }
    >({
      query: ({ branchCode }) => ({
        url: `/retrieveinformativa/${encodeURIComponent(branchCode)}`,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) => {
        const data = normalizePro20Response(response);
        const normalized = normalizeInformativa(data);
        if (!normalized) {
          throw new Error('Invalid informativa response');
        }
        return normalized;
      },
      providesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Branch, id: `informativa-${branchCode}` },
      ],
    }),
  }),
});

export const {
  useGetInformativaByBranchCodeQuery,
  useLazyGetInformativaByBranchCodeQuery,
} = informativaApi;
